import React, { useContext, useEffect, useState } from 'react';
import { Flex, useToast, Heading, Tabs, TabList, TabPanels, Tab, TabPanel } from '@chakra-ui/react';
import { ListingAttributes, ListingsView } from 'src/components/organisms';
import { MarketplaceContext } from 'src/contexts/marketplace';
import { AttributeSelectorProvider } from 'src/contexts/AttributeSelector';
import { Attribute, ThirdPartyQuery } from 'src/api/v1-api';
import { SortOption } from 'src/constants/sort-options';
import useRequest from 'src/utils/hooks/useRequest';
import { useAuth } from 'src/utils/auth';
import { useRouter } from 'next/router';
import SingleListingImportsTable from 'src/components/organisms/single-listing-imports-table';
import { CoverLoader } from 'src/components/atoms';

const ListingApprovals = (): JSX.Element => {
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [queries, setQueries] = useState<ThirdPartyQuery[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const { marketplace, categories } = useContext(MarketplaceContext);
  const { api } = useRequest();
  const toast = useToast();
  const { userLoaded } = useAuth();
  const router = useRouter();

  useEffect(() => {
    if (
      router.isReady &&
      router.query.message &&
      router.query.success === 'true' &&
      !toast.isActive('listing-approval-success')
    ) {
      toast({
        title: 'Success',
        description: router.query.message,
        status: 'success',
        duration: 5000,
        isClosable: true,
        id: 'listing-approval-success',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, router.query]);

  useEffect(() => {
    const fetchQueries = async () => {
      if (!marketplace) return;

      try {
        setIsLoading(true);
        const response = await api.marketplace.query.list(marketplace.id);
        setQueries(response);
      } catch (error) {
        toast({
          title: 'Error fetching Saved Searches',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (userLoaded) {
      fetchQueries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [marketplace, userLoaded]);

  useEffect(() => {
    if (!categories || categories.length === 0) return;
    const sourceAttributes = {
      id: 1,
      options: [
        {
          id: 1,
          name: 'Loma',
          value: 'loma',
          position: 0,
        },
        {
          id: 2,
          name: 'Facebook',
          value: 'facebook',
          position: 1,
        },
        {
          id: 3,
          name: 'Craigslist',
          value: 'craigslist',
          position: 2,
        },
        {
          id: 4,
          name: 'eBay',
          value: 'ebay',
          position: 3,
        },
        {
          id: 5,
          name: 'Google',
          value: 'google',
          position: 4,
        },
        {
          id: 6,
          name: 'Yelp',
          value: 'yelp',
          position: 5,
        },
        {
          id: 7,
          name: 'Other',
          value: 'other',
          position: 6,
        },
      ],
      format: 'multiselect',
      required: false,
      name: 'source',
      main_section_display: false,
      min_value: '',
      max_value: '',
      content_type_name: 'Listing',
      buyer_description: '',
      seller_description: '',
      position: 1,
      sensitive: false,
      filter_display: true,
      filter_min_value: '',
      filter_max_value: '',
      collect_in_modal_submission: false,
    };

    const queriesOptions = queries.map((query) => ({
      id: query.id ?? 0,
      name: `${query.third_party_platform} - ${query.search_term}`,
      value: `${query.id ?? 0}`,
      position: query.id ?? 0,
    }));

    const queriesAttributes = {
      id: 2,
      options: queriesOptions,
      format: 'multiselect',
      required: false,
      name: 'queries',
      main_section_display: false,
      min_value: '',
      max_value: '',
      content_type_name: 'Listing',
      buyer_description: '',
      seller_description: '',
      position: 2,
      sensitive: false,
      filter_display: true,
      filter_min_value: '',
      filter_max_value: '',
      collect_in_modal_submission: false,
    };

    const categoryAttributes = {
      id: 3,
      options: categories.map((category) => ({
        id: category.id,
        name: category.name,
        value: `${category.id}`,
        position: category.position,
        parentId:
          // @ts-ignore
          typeof category.parent === 'number' ? category?.parent : category?.parent?.id ?? null,
      })),
      format: 'tree',
      required: false,
      name: 'category',
      main_section_display: false,
      min_value: '',
      max_value: '',
      content_type_name: 'Listing',
      buyer_description: '',
      seller_description: '',
      position: 3,
      sensitive: false,
      filter_display: true,
      filter_min_value: '',
      filter_max_value: '',
      collect_in_modal_submission: false,
    };

    setAttributes([sourceAttributes, queriesAttributes, categoryAttributes]);
  }, [queries, categories]);

  const [activeTab, setActiveTab] = useState(0);

  return (
    <Flex w="full" direction="column" rowGap={{ base: 6, md: 7, lg: 8 }}>
      <Heading fontSize="2xl" data-test="page-title">
        Listing Approvals
      </Heading>
      <Tabs variant="enclosed" mt={2} index={activeTab} onChange={setActiveTab}>
        <TabList>
          <Tab>Pending Listings</Tab>
          <Tab>Recent Imports</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0}>
            {isLoading ? (
              <CoverLoader />
            ) : (
              <AttributeSelectorProvider
                attributes={attributes}
                useToggle={false}
                defaultViewType="grid"
                sortOptions={((): SortOption[] => {
                  const baseOptions: SortOption[] = ['source-loma', 'ai-sort', 'newest', 'oldest'];
                  const aiEnabled = marketplace?.functional?.enable_ai_sorting;
                  return aiEnabled ? baseOptions : baseOptions.filter((opt) => opt !== 'ai-sort');
                })()}
                maxPrice={0}
              >
                <ListingAttributes />
                <ListingsView
                  fetchUrl={`${process.env.NEXT_PUBLIC_API_HOST}/pending_listings/${marketplace?.id}`}
                  requiresAuth={true}
                  inventoryDisplay={'4_2_grid'}
                  paginationStyle={''}
                  resultsPerPage={38}
                  actionButtons="pendingListings"
                  propNumDisplayListings={32}
                />
              </AttributeSelectorProvider>
            )}
          </TabPanel>
          <TabPanel px={0}>
            <SingleListingImportsTable isActive={activeTab === 1} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex>
  );
};

export default ListingApprovals;
